import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  fetchAgendaItemDetails,
  updateAgendaItem,
} from "../../methods/agenda/agendaItemRequests";
import {
  formatDateTime,
  toApiDateTimeValue,
  toDateTimeLocalValue,
} from "./agendaDateUtils";

const SOURCE_LABELS = {
  appointment: "Appointment",
  class_booking: "Class Booking",
  class_session: "Class Session",
};

const STATUS_OPTIONS = {
  appointment: [
    { value: "scheduled", label: "Scheduled" },
    { value: "confirmed", label: "Confirmed" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "no_show", label: "No Show" },
  ],
  class_booking: [
    { value: "booked", label: "Booked" },
    { value: "checked_in", label: "Checked In" },
    { value: "cancelled", label: "Cancelled" },
    { value: "no_show", label: "No Show" },
  ],
  class_session: [
    { value: "scheduled", label: "Scheduled" },
    { value: "cancelled", label: "Cancelled" },
    { value: "completed", label: "Completed" },
  ],
};

const editableFieldsBySource = {
  appointment: [
    { name: "title", label: "Title", type: "text" },
    { name: "starts_at", label: "Starts At", type: "datetime" },
    { name: "ends_at", label: "Ends At", type: "datetime" },
    {
      name: "location_type",
      label: "Location Type",
      type: "select",
      options: [
        { value: "club", label: "Club" },
        { value: "online", label: "Online" },
        { value: "phone", label: "Phone" },
      ],
    },
    { name: "location_notes", label: "Location Notes", type: "multiline", minRows: 2 },
    { name: "description", label: "Description", type: "multiline", minRows: 3 },
    { name: "internal_notes", label: "Internal Notes", type: "multiline", minRows: 3 },
  ],
  class_booking: [
    { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS.class_booking },
    { name: "booked_at", label: "Booked At", type: "datetime" },
    { name: "cancelled_at", label: "Cancelled At", type: "datetime" },
    { name: "checked_in_at", label: "Checked In At", type: "datetime" },
    { name: "notes", label: "Notes", type: "multiline", minRows: 3 },
  ],
  class_session: [
    { name: "starts_at", label: "Starts At", type: "datetime" },
    { name: "ends_at", label: "Ends At", type: "datetime" },
    { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS.class_session },
    { name: "capacity", label: "Capacity", type: "number" },
  ],
};

const detailSectionsBySource = {
  appointment: [
    {
      title: "Appointment",
      fields: [
        { path: "id", label: "ID" },
        { path: "status", label: "Status" },
        { path: "type.name", label: "Appointment Type" },
        { path: "type.duration_minutes", label: "Default Duration (Minutes)" },
        { path: "title", label: "Title" },
        { path: "starts_at", label: "Starts At", type: "datetime" },
        { path: "ends_at", label: "Ends At", type: "datetime" },
        { path: "location_type", label: "Location Type" },
        { path: "location_notes", label: "Location Notes" },
        { path: "description", label: "Description" },
        { path: "internal_notes", label: "Internal Notes" },
        { path: "cancelled_at", label: "Cancelled At", type: "datetime", hideWhenEmpty: true },
        { path: "completed_at", label: "Completed At", type: "datetime", hideWhenEmpty: true },
      ],
    },
    {
      title: "People",
      fields: [
        { path: "customer.name", label: "Member" },
        { path: "customer.email", label: "Member Email" },
        { path: "staff.name", label: "Staff" },
        { path: "staff.email", label: "Staff Email" },
        { path: "createdBy.name", label: "Created By" },
        { path: "createdBy.email", label: "Creator Email" },
      ],
    },
    {
      title: "Location",
      fields: [
        { path: "room.name", label: "Room" },
        { path: "room.code", label: "Room Code" },
        { path: "room_id", label: "Room ID", hideWhenEmpty: true },
      ],
    },
    {
      title: "Identifiers",
      fields: [
        { path: "appointment_type_id", label: "Appointment Type ID" },
        { path: "customer_user_id", label: "Member User ID" },
        { path: "staff_user_id", label: "Staff User ID" },
        { path: "created_by_user_id", label: "Created By User ID", hideWhenEmpty: true },
      ],
    },
  ],
  class_booking: [
    {
      title: "Booking",
      fields: [
        { path: "id", label: "ID" },
        { path: "uuid", label: "UUID" },
        { path: "status", label: "Status" },
        { path: "booked_at", label: "Booked At", type: "datetime" },
        { path: "cancelled_at", label: "Cancelled At", type: "datetime", hideWhenEmpty: true },
        { path: "checked_in_at", label: "Checked In At", type: "datetime", hideWhenEmpty: true },
        { path: "notes", label: "Notes" },
      ],
    },
    {
      title: "Member",
      fields: [
        { path: "user.name", label: "Member" },
        { path: "user.email", label: "Member Email" },
        { path: "user_id", label: "Member User ID" },
      ],
    },
    {
      title: "Class",
      fields: [
        { path: "classSession.gymClass.name", label: "Class Name" },
        { path: "classSession.status", label: "Session Status" },
        { path: "classSession.starts_at", label: "Session Starts At", type: "datetime" },
        { path: "classSession.ends_at", label: "Session Ends At", type: "datetime" },
        { path: "class_session_id", label: "Class Session ID" },
      ],
    },
    {
      title: "Identifiers",
      fields: [
        { path: "entitlement_id", label: "Entitlement ID", hideWhenEmpty: true },
      ],
    },
  ],
  class_session: [
    {
      title: "Session",
      fields: [
        { path: "id", label: "ID" },
        { path: "status", label: "Status" },
        { path: "starts_at", label: "Starts At", type: "datetime" },
        { path: "ends_at", label: "Ends At", type: "datetime" },
        { path: "capacity", label: "Capacity" },
      ],
    },
    {
      title: "Class",
      fields: [
        { path: "gymClass.name", label: "Class Name" },
        { path: "class_id", label: "Class ID" },
      ],
    },
    {
      title: "Staff",
      fields: [
        { path: "professor.name", label: "Professor" },
        { path: "professor.email", label: "Professor Email" },
        { path: "professor_id", label: "Professor User ID", hideWhenEmpty: true },
      ],
    },
    {
      title: "Location",
      fields: [
        { path: "room.name", label: "Room" },
        { path: "room.code", label: "Room Code" },
        { path: "locationZone.name", label: "Location Zone" },
        { path: "room_id", label: "Room ID", hideWhenEmpty: true },
        { path: "location_zone_id", label: "Location Zone ID", hideWhenEmpty: true },
      ],
    },
    {
      title: "Schedule",
      fields: [
        { path: "schedule.weekday", label: "Weekday" },
        { path: "schedule.start_time", label: "Scheduled Start" },
        { path: "schedule.end_time", label: "Scheduled End" },
        { path: "schedule_id", label: "Schedule ID", hideWhenEmpty: true },
      ],
    },
  ],
};

function getValueAtPath(record, path) {
  return path.split(".").reduce((current, segment) => {
    if (current === null || current === undefined) {
      return undefined;
    }

    return current[segment];
  }, record);
}

function isEmptyValue(value) {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === "string") {
    return value.trim() === "";
  }

  return false;
}

function formatFieldValue(field, value) {
  if (isEmptyValue(value)) {
    return "Not set";
  }

  if (field?.type === "datetime") {
    return formatDateTime(value);
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    return value.join(", ");
  }

  return String(value);
}

function buildInitialFormValues(source, record) {
  const fields = editableFieldsBySource[source] || [];

  return fields.reduce((nextValues, field) => {
    const rawValue = record?.[field.name];

    if (field.type === "datetime") {
      nextValues[field.name] = toDateTimeLocalValue(rawValue);
      return nextValues;
    }

    if (field.type === "number") {
      nextValues[field.name] = rawValue ?? "";
      return nextValues;
    }

    nextValues[field.name] = rawValue ?? "";
    return nextValues;
  }, {});
}

function buildUpdatePayload(source, formValues) {
  const fields = editableFieldsBySource[source] || [];

  return fields.reduce((payload, field) => {
    const rawValue = formValues[field.name];

    if (field.type === "datetime") {
      payload[field.name] = rawValue ? toApiDateTimeValue(rawValue) : null;
      return payload;
    }

    if (field.type === "number") {
      payload[field.name] = rawValue === "" ? null : Number(rawValue);
      return payload;
    }

    payload[field.name] = rawValue === "" ? null : rawValue;
    return payload;
  }, {});
}

function buildHeaderTitle(item, details) {
  if (item?.source === "appointment") {
    return details?.title || details?.type?.name || item?.title || "Appointment";
  }

  if (item?.source === "class_booking") {
    return details?.classSession?.gymClass?.name || item?.title || "Class Booking";
  }

  if (item?.source === "class_session") {
    return details?.gymClass?.name || item?.title || "Class Session";
  }

  return item?.title || "Agenda Item";
}

function buildHeaderSubtitle(item, details) {
  if (item?.source === "appointment") {
    const participants = [details?.staff?.name, details?.customer?.name].filter(Boolean);
    return participants.length ? participants.join(" / ") : item?.subtitle || "Appointment";
  }

  if (item?.source === "class_booking") {
    return details?.user?.name ? `Booked by ${details.user.name}` : item?.subtitle || "Class booking";
  }

  if (item?.source === "class_session") {
    return details?.professor?.name
      ? `Assigned to ${details.professor.name}`
      : item?.subtitle || "Class session";
  }

  return item?.subtitle || "Agenda item";
}

export default function AppointmentDetailsDrawer({
  open,
  item,
  color,
  onClose,
  onSaved,
}) {
  const [loading, setLoading] = React.useState(false);
  const [detailsError, setDetailsError] = React.useState("");
  const [details, setDetails] = React.useState(null);
  const [editMode, setEditMode] = React.useState(false);
  const [formValues, setFormValues] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");
  const [validationErrors, setValidationErrors] = React.useState({});

  const editableFields = editableFieldsBySource[item?.source] || [];
  const detailSections = detailSectionsBySource[item?.source] || [];
  const canEdit = editableFields.length > 0;

  React.useEffect(() => {
    if (!open || !item?.source || !item?.source_id) {
      setLoading(false);
      setDetailsError("");
      setDetails(null);
      setEditMode(false);
      setFormValues({});
      setSaveError("");
      setValidationErrors({});
      return;
    }

    let isActive = true;

    const loadDetails = async () => {
      setLoading(true);
      setDetailsError("");
      setDetails(null);
      setEditMode(false);
      setSaveError("");
      setValidationErrors({});

      const response = await fetchAgendaItemDetails(item);

      if (!isActive) {
        return;
      }

      if (!response?.success) {
        setDetailsError(response?.error || "Failed to load this agenda item.");
        setLoading(false);
        return;
      }

      setDetails(response.data || null);
      setFormValues(buildInitialFormValues(item.source, response.data || null));
      setLoading(false);
    };

    loadDetails();

    return () => {
      isActive = false;
    };
  }, [open, item?.id, item?.source, item?.source_id]);

  const handleEdit = React.useCallback(() => {
    setFormValues(buildInitialFormValues(item?.source, details));
    setValidationErrors({});
    setSaveError("");
    setEditMode(true);
  }, [details, item?.source]);

  const handleSave = React.useCallback(async () => {
    if (!item || !canEdit) {
      return;
    }

    setSaving(true);
    setSaveError("");
    setValidationErrors({});

    const response = await updateAgendaItem(item, buildUpdatePayload(item.source, formValues));

    if (!response?.success) {
      setSaveError(response?.error || "Failed to save changes.");
      setValidationErrors(response?.validationErrors || {});
      setSaving(false);
      return;
    }

    setDetails(response.data || null);
    setFormValues(buildInitialFormValues(item.source, response.data || null));
    setEditMode(false);
    setSaving(false);

    if (typeof onSaved === "function") {
      Promise.resolve(onSaved()).catch(() => {});
    }
  }, [canEdit, formValues, item, onSaved]);

  const title = buildHeaderTitle(item, details);
  const subtitle = buildHeaderSubtitle(item, details);
  const status = details?.status || item?.status || "scheduled";

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 360, sm: 520 }, maxWidth: "100vw", height: "100%", overflowY: "auto", p: 3 }}>
        {!item ? null : (
          <>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                backgroundColor: `${color}18`,
                borderLeft: `4px solid ${color}`,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap" }}>
                <Chip label={SOURCE_LABELS[item.source] || item.source} size="small" />
                <Chip label={status} size="small" variant="outlined" />
              </Stack>

              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} sx={{ my: 2, flexWrap: "wrap" }}>
              {(item.actions || []).map((action) => (
                <Chip key={action} label={action} size="small" variant="outlined" />
              ))}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
              {editMode ? (
                <>
                  <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setFormValues(buildInitialFormValues(item.source, details));
                      setValidationErrors({});
                      setSaveError("");
                      setEditMode(false);
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </>
              ) : canEdit ? (
                <Button variant="contained" onClick={handleEdit} disabled={loading || !details}>
                  Edit
                </Button>
              ) : null}

              <Button variant="text" onClick={onClose}>
                Close
              </Button>
            </Stack>

            {loading ? (
              <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : null}

            {!loading && detailsError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {detailsError}
              </Alert>
            ) : null}

            {!loading && !detailsError && saveError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            ) : null}

            {!loading && !detailsError && details ? (
              <>
                {editMode ? (
                  <Stack spacing={2}>
                    {editableFields.map((field) => {
                      if (field.type === "select") {
                        return (
                          <TextField
                            key={field.name}
                            select
                            fullWidth
                            size="small"
                            label={field.label}
                            value={formValues[field.name] ?? ""}
                            onChange={(event) => {
                              setFormValues((current) => ({
                                ...current,
                                [field.name]: event.target.value,
                              }));
                            }}
                            error={Boolean(validationErrors[field.name])}
                            helperText={validationErrors[field.name]?.[0] || " "}
                          >
                            {field.options.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        );
                      }

                      return (
                        <TextField
                          key={field.name}
                          fullWidth
                          size="small"
                          label={field.label}
                          type={field.type === "datetime" ? "datetime-local" : field.type === "number" ? "number" : "text"}
                          multiline={field.type === "multiline"}
                          minRows={field.type === "multiline" ? (field.minRows || 2) : undefined}
                          value={formValues[field.name] ?? ""}
                          onChange={(event) => {
                            setFormValues((current) => ({
                              ...current,
                              [field.name]: event.target.value,
                            }));
                          }}
                          InputLabelProps={field.type === "datetime" ? { shrink: true } : undefined}
                          error={Boolean(validationErrors[field.name])}
                          helperText={validationErrors[field.name]?.[0] || " "}
                        />
                      );
                    })}
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    {detailSections.map((section) => (
                      <Box key={section.title}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {section.title}
                        </Typography>
                        <Divider sx={{ my: 1.5 }} />
                        <Stack spacing={1.5}>
                          {section.fields.map((field) => {
                            const value = getValueAtPath(details, field.path);

                            if (field.hideWhenEmpty && isEmptyValue(value)) {
                              return null;
                            }

                            return (
                              <Box key={field.path}>
                                <Typography variant="caption" color="text.secondary">
                                  {field.label}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: field.type === "datetime" ? "normal" : "pre-wrap",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {formatFieldValue(field, value)}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    ))}

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Timeline Summary
                      </Typography>
                      <Typography variant="body2">
                        {formatDateTime(item.starts_at)} to {formatDateTime(item.ends_at)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.location || "No location set"}
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </>
            ) : null}
          </>
        )}
      </Box>
    </Drawer>
  );
}
